<script setup lang="ts">
/**
 * The dropdown every list filter and the project picker use.
 *
 * Two things a native `<select>` cannot do, both of which people hit here:
 *
 *  1. TYPE TO NARROW. A native select only jumps by first letter, so finding
 *     one project among dozens means scrolling a list ordered by nothing you
 *     can see. Options are filtered in memory — the whole set is loaded.
 *
 *  2. LOOK DIFFERENT WHEN IT IS NARROWING. Filter values persist across
 *     navigation, and a select reading "Publicado" looks exactly like one
 *     reading "Todos los estados": same border, same weight. People conclude
 *     the list is empty when it is merely filtered. A set filter therefore
 *     carries the accent plus an inline clear button.
 *
 * The highlight is tied to `clearable` on purpose: it means "you are looking
 * at a subset, and here is the way back". A required selector with no empty
 * state — the project picker — passes `:clearable="false"` and stays neutral.
 */
import { Check, ChevronDown, X } from '@lucide/vue'
import { computed, nextTick, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import UiFloating from './UiFloating.vue'

export interface FilterOption {
  value: string
  label: string
  /** Secondary text, searched but rendered dimmer. */
  hint?: string
}

const props = withDefaults(
  defineProps<{
    modelValue: string | null
    options: FilterOption[]
    /** Shown when nothing is selected, and as the "clear" row's label. */
    placeholder: string
    /** Whether the empty value is reachable. False also drops the accent. */
    clearable?: boolean
    disabled?: boolean
    /** Width utility for the trigger. */
    widthClass?: string
  }>(),
  { clearable: true, disabled: false, widthClass: 'w-full sm:w-48' },
)

const emit = defineEmits<{ (e: 'update:modelValue', value: string | null): void }>()

const { t } = useI18n()

/** Above this many options, typing beats scrolling. */
const SEARCH_THRESHOLD = 8

const open = ref(false)
const search = ref('')
const activeIndex = ref(-1)
const rootEl = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)
const listEl = ref<HTMLElement | null>(null)

/**
 * The popup lives in `body`, so the combobox and its listbox are no longer
 * relatives; `aria-controls` is an id reference and is the one relationship
 * that survives that.
 */
const listboxId = useId()

/** Case- and accent-insensitive, so "Periódico" is found by typing "periodico". */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

const showSearch = computed(() => props.options.length > SEARCH_THRESHOLD)

const selected = computed(() =>
  props.modelValue === null
    ? null
    : (props.options.find((option) => option.value === props.modelValue) ?? null),
)

/**
 * A value that is set but absent from `options` — a filter persisted from a
 * session where the option still existed (an archived project, a deleted
 * asset). Saying so is better than falling back to the placeholder, which
 * would read as "not filtering" while the list stays narrowed.
 */
const isOrphanValue = computed(() => props.modelValue !== null && selected.value === null)

const triggerLabel = computed(() => {
  if (selected.value) return selected.value.label
  if (isOrphanValue.value) return t('common.filterSelect.unknownValue')
  return props.placeholder
})

const hasValue = computed(() => props.modelValue !== null)
const showsAsActive = computed(() => hasValue.value && props.clearable)

const filtered = computed(() => {
  const term = normalize(search.value.trim())
  if (!term) return props.options
  return props.options.filter(
    (option) =>
      normalize(option.label).includes(term) ||
      (option.hint !== undefined && normalize(option.hint).includes(term)),
  )
})

/**
 * Called by the trigger, by a pick, and by `UiFloating` — outside-pointerdown
 * and Escape are its job now, because it is the only thing that can tell a
 * pointerdown on a panel nested inside this one from one truly outside.
 */
function close(): void {
  open.value = false
  activeIndex.value = -1
}

function scrollActiveIntoView(): void {
  listEl.value
    ?.querySelector<HTMLElement>('[data-active="true"]')
    ?.scrollIntoView({ block: 'nearest' })
}

function openDropdown(): void {
  if (props.disabled) return
  open.value = true
  search.value = ''
  // Start on the current selection so Enter re-picks it instead of jumping to
  // the top of the list.
  activeIndex.value = filtered.value.findIndex((option) => option.value === props.modelValue)
  void nextTick(() => {
    if (showSearch.value) inputEl.value?.focus()
    scrollActiveIntoView()
  })
}

function pick(value: string | null): void {
  emit('update:modelValue', value)
  close()
}

function move(delta: number): void {
  const count = filtered.value.length
  if (count === 0) return
  // Clamped rather than wrapping: at the ends the next arrow press should do
  // nothing visible instead of teleporting to the other end of a long list.
  activeIndex.value = Math.max(0, Math.min(count - 1, activeIndex.value + delta))
  void nextTick(scrollActiveIntoView)
}

function onTriggerKeydown(event: KeyboardEvent): void {
  if (open.value) return
  if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    openDropdown()
  }
}

function onKeydown(event: KeyboardEvent): void {
  switch (event.key) {
    case 'Escape':
      event.preventDefault()
      close()
      break
    case 'ArrowDown':
      event.preventDefault()
      move(1)
      break
    case 'ArrowUp':
      event.preventDefault()
      move(-1)
      break
    case 'Enter': {
      event.preventDefault()
      const option = filtered.value[activeIndex.value]
      if (option) pick(option.value)
      break
    }
    default:
      break
  }
}

// Typing re-filters, so the previously active row may be gone or be a
// different option; land on the first match instead of a stale index.
watch(search, () => {
  activeIndex.value = filtered.value.length > 0 ? 0 : -1
})
</script>

<template>
  <div ref="rootEl" class="min-w-0" :class="widthClass">
    <!-- The trigger carries the INPUT classes, not `select`: daisyUI paints
         `.select`'s arrow with background images on any element bearing the
         class, which would stack a second chevron behind our own. `.input` is
         the same height and has no painted arrow. -->
    <button
      type="button"
      role="combobox"
      class="input input-sm input-bordered flex w-full min-w-0 cursor-pointer items-center gap-1.5 text-left font-normal"
      :class="
        showsAsActive
          ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
          : 'text-base-content'
      "
      :disabled="disabled"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="listboxId"
      :aria-label="placeholder"
      @click="open ? close() : openDropdown()"
      @keydown="onTriggerKeydown"
    >
      <span class="flex-1 truncate" :class="hasValue ? '' : 'text-subtle'">{{ triggerLabel }}</span>

      <!-- Clearing is the way out of a filter people did not realise was on,
           so it sits on the control itself, not only inside the dropdown. -->
      <span
        v-if="showsAsActive && !disabled"
        role="button"
        tabindex="-1"
        class="shrink-0 rounded-full p-0.5 opacity-70 hover:bg-primary/20 hover:opacity-100"
        :aria-label="t('common.filterSelect.clear')"
        :title="t('common.filterSelect.clear')"
        @click.stop="pick(null)"
        @keydown.enter.stop.prevent="pick(null)"
      >
        <X class="size-3" />
      </span>
      <ChevronDown v-else class="size-3.5 shrink-0 opacity-50" />
    </button>

    <!-- Not `absolute` inside the trigger's box any more: the project picker
         lives in a 52px top bar and the list filters live in panes that are
         `overflow-hidden`, both of which cut the popup off. `UiFloating`
         teleports it to `body` and anchors it to `rootEl`, so it is measured
         from the trigger's full width and a pointerdown on the trigger still
         counts as inside — otherwise the dismissal would fire first and the
         click would reopen what it meant to close.

         FOLLOW rather than close on scroll: a filter bar can sit in a
         scrolling pane, and the popup's own list scrolls too. Closing on
         scroll would shut the list the moment somebody wheeled it. -->
    <UiFloating
      :anchor="rootEl"
      :open="open"
      side="bottom"
      align="start"
      match-anchor-width
      class="rounded-box border-hairline-strong bg-base-100 min-w-52 overflow-hidden border shadow-[0_10px_30px_rgba(0,0,0,.5)]"
      data-test="filter-select-popup"
      @close="close()"
      @keydown="onKeydown"
    >
      <label v-if="showSearch" class="flex items-center gap-2 border-b border-hairline px-2.5">
        <input
          ref="inputEl"
          v-model="search"
          type="text"
          class="input input-sm w-full border-0 bg-transparent px-0 focus:outline-none"
          :placeholder="t('common.filterSelect.searchPlaceholder')"
        />
      </label>

      <div :id="listboxId" ref="listEl" role="listbox" class="max-h-60 overflow-y-auto py-1">
        <!-- The "no filter" row, present even while searching so the way back
             is never more than one keystroke away. -->
        <button
          v-if="clearable"
          type="button"
          role="option"
          :aria-selected="!hasValue"
          class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-base-200"
          :class="hasValue ? '' : 'font-medium'"
          @click="pick(null)"
        >
          <Check class="size-3.5 shrink-0" :class="hasValue ? 'invisible' : ''" />
          <span class="truncate">{{ placeholder }}</span>
        </button>

        <button
          v-for="(option, index) in filtered"
          :key="option.value"
          type="button"
          role="option"
          :aria-selected="option.value === modelValue"
          :data-active="index === activeIndex ? 'true' : 'false'"
          class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm"
          :class="[
            index === activeIndex ? 'bg-base-200' : 'hover:bg-base-200/60',
            option.value === modelValue ? 'font-medium text-primary' : '',
          ]"
          @click="pick(option.value)"
          @mousemove="activeIndex = index"
        >
          <Check class="size-3.5 shrink-0" :class="option.value === modelValue ? '' : 'invisible'" />
          <span class="truncate">{{ option.label }}</span>
          <span v-if="option.hint" class="truncate text-xs text-subtle">{{ option.hint }}</span>
        </button>

        <p v-if="filtered.length === 0" class="px-3 py-2 text-xs italic text-subtle">
          {{ t('common.filterSelect.noResults') }}
        </p>
      </div>
    </UiFloating>
  </div>
</template>
